/* eslint-disable import/no-webpack-loader-syntax */
import { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";
import { useGetMyInventoryQuery } from "../../services/cardtrader/cardtraderApi";
import { selectGame, selectToken } from "../../store/slices/global";
import { LoadingOverlay } from "../../components/common/LoadingOverley/LoadingOverley";
import {
  CardSelected,
  MultiSelectConditionType,
  MultiSelectLanguageType,
} from "../../type";
import { CardBoard } from "../../components/CardBoard/CardBoard";
import { Box, FlexBox } from "../../components/primitives";
import { FilterV2 } from "../../components/Filters/FilterV2";
import { handleFilterAndSortCards } from "../../utils";
import { ModalPricing } from "../../components/ModalPricing/ModalPricing";
import { ModalChooseCardDetail } from "../../components/UploadCardsPageComponents/ModalChooseCardDetail/ModalChooseCardDetail";
import { ModalPublishChange } from "../../components/MyInventoryPageComponents/ModalPublishChange/ModalPublishChange";
import { useCards } from "../../context/CardsContext";
import { Header } from "../../components/MyInventoryPageComponents/Header/Header";
import toast from "react-hot-toast";
import {
  CardToEditBatchCreateRequestType,
  CardTraderConditionValueType,
  CardTraderLanguageValueType,
} from "magic-shared-types";
import { useBatchCreateCardToEditMutation } from "../../services/myServer/cardToEdit";
import { SideMenuWrapper } from "./myInventoryPage.styled";
import { SubHeader } from "../../components/UploadCardsPageComponents/SubHeader/SubHeader";

export const MyInventoryPage = () => {
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [cardsBoard, setCardsBoard] = useState<CardSelected[]>([]);
  const [cardSelected, setCardsSelected] = useState<CardSelected | null>(null);
  const [cardsClicked, setCardsClicked] = useState<string[]>([]);
  const [openModalChoosePrice, setOpenModalChoosePrice] = useState<
    string | null
  >(null);
  const [editCard, setEditCard] = useState<string | null>(null);
  const [loadingCardBoard, setLoadingCardBoard] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchTextComment, setSearchTextComment] = useState("");
  const [sortOption, setSortOption] = useState("name-asc");
  const [filterOption, setFilterOption] = useState("none");
  const [priceMin, setPriceMin] = useState<number | undefined>();
  const [priceMax, setPriceMax] = useState<number | undefined>();
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [debouncedSearchTextComment, setDebouncedSearchTextComment] =
    useState("");
  const [languagesFilter, setLanguagesFilter] = useState<
    MultiSelectLanguageType[]
  >([]);
  const [conditionsFilter, setConditionsFilter] = useState<
    MultiSelectConditionType[]
  >([]);
  const [isOpenModalPublishChange, setIsOpenModalPublishChange] =
    useState(false);
  const game = useSelector(selectGame);
  const token = useSelector(selectToken);
  const foundCardComplexArray = useCards();
  const [batchCreateCardToEdit] = useBatchCreateCardToEditMutation();

  const { data, isLoading } = useGetMyInventoryQuery(
    { token },
    { skip: !token }
  );

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchText(value);
      }, 300),
    []
  );

  const cardsBoardFiltered = useMemo(() => {
    return handleFilterAndSortCards({
      debouncedSearchText,
      debouncedSearchTextComment,
      cardsBoard,
      sortOption,
      filterOption,
      foundCardComplexArray,
      priceMin,
      priceMax,
      game_id: game.id,
      languagesFilter: languagesFilter.map((lang) => lang.value),
      conditionsFilter: conditionsFilter.map((con) => con.value),
    });
  }, [
    debouncedSearchText,
    cardsBoard,
    sortOption,
    filterOption,
    foundCardComplexArray,
    priceMin,
    priceMax,
    game,
    languagesFilter,
    conditionsFilter,
    debouncedSearchTextComment,
  ]);

  useEffect(() => {
    if (editCard) {
      const cardFoundToEdit = cardsBoard.find((card) => card.uid === editCard);
      if (cardFoundToEdit) setCardsSelected(cardFoundToEdit);
    }
  }, [cardsBoard, editCard]);

  useEffect(() => {
    if (openModalChoosePrice) {
      const cardFoundToEdit = cardsBoard.find(
        (card) => card.uid === openModalChoosePrice
      );
      if (cardFoundToEdit) setCardsSelected(cardFoundToEdit);
    }
  }, [cardsBoard, openModalChoosePrice]);

  useEffect(() => {
    handleSearchChange(searchText);
  }, [searchText, handleSearchChange]);

  useEffect(() => {
    return () => {
      handleSearchChange.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChangeComment = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTextComment(value);
      }, 300),
    []
  );

  useEffect(() => {
    handleSearchChangeComment(searchTextComment);
  }, [searchTextComment, handleSearchChangeComment]);

  useEffect(() => {
    if (!data || !game) {
      return;
    }
    setLoadingCardBoard(true);

    const worker = new Worker(
      new URL(
        "../../workers/InventoryProcessorWorker.worker.ts",
        import.meta.url
      ),
      { type: "module" }
    );

    worker.postMessage({
      name: "workerMyInvetoryPage",
      data: data.filter((dat) => dat.game_id === game.id),
      game: game.card_trader_game,
      gameId: game.id,
    });

    worker.onmessage = (event: any) => {
      setCardsBoard(event.data);
      setLoadingCardBoard(false);
      worker.terminate();
    };

    return () => worker.terminate();
  }, [data, game]);

  console.log("cardsBoard", cardsBoard);

  const onClickEditHandler = async () => {
    setIsApiLoading(true);
    const BATCH_SIZE = 250;

    const splitIntoEditBatches = (
      array: CardSelected[],
      size: number
    ): CardToEditBatchCreateRequestType[][] => {
      const batches = [];
      for (let i = 0; i < array.length; i += size) {
        const batch = array.slice(i, i + size).map((card) => {
          const cardFound = foundCardComplexArray[card.id];
          return {
            cardtrader_id: card.cardTraderId as number,
            user_id: 1,
            card_name: card.name,
            blue_print_id: card.id,
            game_id: game.id,
            card_market_id: cardFound.card_market_ids[0],

            old_language: card.originalLanguage as CardTraderLanguageValueType,
            old_quantity: card.originalQuantity as number,
            old_condition:
              card.originalCondition as CardTraderConditionValueType,
            old_price: card.originalPrice as number,
            old_is_foil: card.originalFinish === "foil" || false,
            old_is_signed: card.originalSigned || false,
            old_is_altered: card.originalAltered || false,
            old_description: card.originalDescription || "",

            new_language: card.language,
            new_quantity: card.quantity,
            new_condition: card.condition,
            new_price: card.price,
            new_is_foil: card.finish === "foil" || false,
            new_is_signed: card.signed || false,
            new_is_altered: card.altered || false,
            new_description: card.description || "",
          };
        });
        batches.push(batch);
      }
      return batches;
    };

    const editedCardsBoard = cardsBoardFiltered?.filter(
      (card) => cardsClicked.includes(card.uid) && card.hasBeenEdited
    );

    const batches = splitIntoEditBatches(editedCardsBoard, BATCH_SIZE);

    for (let i = 0; i < batches.length; i++) {
      const currentBatch = batches[i];

      try {
        await batchCreateCardToEdit({ cards: currentBatch }).unwrap();

        console.log(`✅ Batch ${i + 1}/${batches.length} edited`);
      } catch (err) {
        console.error(`❌ Edit batch ${i + 1} failed`, err);
        toast.error(`Edit batch ${i + 1} failed`);
      }
    }

    toast.success(
      "Changes saved! You can review the transaction in your Edit History."
    );
    setIsApiLoading(false);
  };

  console.log("cardBoardFiltered", cardsBoardFiltered);

  const renderRightModal = () => {
    if (cardSelected && openModalChoosePrice) {
      return (
        <ModalPricing
          setOpenModalChoosePrice={setOpenModalChoosePrice}
          cardSelected={cardSelected}
          setCardsSelected={setCardsSelected}
          setCardsBoard={setCardsBoard}
          openModalChoosePrice={openModalChoosePrice}
          cardsBoard={cardsBoardFiltered}
        />
      );
    }

    if (cardSelected && editCard) {
      return (
        <ModalChooseCardDetail
          setCardsSelected={setCardsSelected}
          cardsBoard={cardsBoardFiltered}
          setCardsBoard={setCardsBoard}
          cardSelected={cardSelected}
          editCard={editCard}
          setEditCard={setEditCard}
        />
      );
    }
  };

  if (isLoading || loadingCardBoard || isApiLoading) {
    return <LoadingOverlay />;
  }

  return (
    <>
      {renderRightModal()}
      {isOpenModalPublishChange && (
        <ModalPublishChange
          setIsOpenModalPublishChange={setIsOpenModalPublishChange}
          cardsBoard={cardsBoardFiltered?.filter(
            (card) => cardsClicked.includes(card.uid) && card.hasBeenEdited
          )}
          onClickEditProductHandler={onClickEditHandler}
        />
      )}
      <Box css={{ backgroundColor: "#f0fdf4", minHeight: "100vh" }}>
        <FlexBox>
          <SideMenuWrapper>
            <SubHeader
              cardsBoard={cardsBoard}
              hasFailedCards={
                !!cardsBoard?.filter((card) => card.hasPriceIssue).length
              }
            />
            <FilterV2
              filterOption={filterOption}
              sortOption={sortOption}
              searchText={searchText}
              setFilterOption={setFilterOption}
              setSearchText={setSearchText}
              setSortOption={setSortOption}
              priceMin={priceMin}
              priceMax={priceMax}
              setPriceMin={setPriceMin}
              setPriceMax={setPriceMax}
              setLanguagesFilter={setLanguagesFilter}
              setConditionsFilter={setConditionsFilter}
              languagesFilter={languagesFilter}
              conditionsFilter={conditionsFilter}
              searchTextComment={searchTextComment}
              setSearchTextComment={setSearchTextComment}
            />
          </SideMenuWrapper>
          <Box css={{ width: "calc(100% - 250px)" }}>
            <Header
              hasEditedCards={!!cardsBoard?.find((card) => card.hasBeenEdited)}
              setIsOpenModalPublishChange={setIsOpenModalPublishChange}
              cardsBoard={cardsBoard}
              cardsBoardCanDoOpeation={cardsBoardFiltered?.filter((card) =>
                cardsClicked.includes(card.uid)
              )}
              setCardsBoard={setCardsBoard}
              isCardsBoardEmpty={
                !cardsBoardFiltered?.filter((card) =>
                  cardsClicked.includes(card.uid)
                ).length
              }
              cardsBoardFiltered={cardsBoardFiltered}
              cardsClicked={cardsClicked}
              setCardsClicked={setCardsClicked}
            />
            <CardBoard
              cardsBoard={cardsBoard}
              cardsBoardFiltered={cardsBoardFiltered}
              setCardsBoard={setCardsBoard}
              removeDelete
              setEditCard={setEditCard}
              setOpenModalChoosePrice={setOpenModalChoosePrice}
              hasAction={false}
              setCardsClicked={setCardsClicked}
              cardsClicked={cardsClicked}
            />
          </Box>
        </FlexBox>
      </Box>
    </>
  );
};
